import { useMemo } from 'react';
import { useImportExportApi } from '../../../clients/quipli-service/import-export/import-export-api';
import { ImportExportAdapter } from '../../../components/import-export/types';
import { DateTime } from 'luxon';
import { Radio } from '../../../components/radio/radio';
import { FieldLabel } from '../../../components/field/field';
import { Typography } from '../../../components/typography/typography';
import { toast } from '../../../util/toasts';

type PartsImportParams = {
  isStockAdditive: boolean;
};
type PartsExportParams = Record<string, never>;

export const usePartsImportExportAdapter = (
  fetchCallback: () => void,
): ImportExportAdapter<PartsImportParams, PartsExportParams> => {
  const api = useImportExportApi();

  return useMemo(
    () => ({
      typeLabel: 'parts',
      fileAccept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',

      defaultExportFileName: () => `Parts_${DateTime.local().toFormat('D').replaceAll('/', '-')}`,

      export: () => api.exportParts(),
      import: (file: File, params: PartsImportParams) => api.importParts(file, params.isStockAdditive),
      exportSample: () => api.exportPartsSample(),

      onImportSuccess: () => {
        toast('File uploaded successfully. You will received an email when the import is complete.', 'success');
        fetchCallback();
      },
      onImportError: () => {
        toast('Error importing parts', 'error');
        fetchCallback();
      },
      onExportSuccess: () => {
        fetchCallback();
      },
      onExportError: () => {
        toast('Error exporting parts', 'error');
        fetchCallback();
      },

      renderExportFields: () => <></>,
      renderImportFields: ({
        params,
        setParams,
      }: {
        params: PartsImportParams;
        setParams: (params: PartsImportParams) => void;
      }) => (
        <div className="flex flex-col mt-4">
          <FieldLabel label="Sale Stock Type" isRequired />
          <div className="mt-2">
            <Radio
              name="stock-type"
              isChecked={params.isStockAdditive}
              onChange={(isChecked) => setParams({ ...params, isStockAdditive: isChecked })}
              label="Add to existing location stock"
              type="white"
              className="mb-1"
            />
            <Radio
              name="stock-type"
              isChecked={!params.isStockAdditive}
              onChange={(isChecked) => setParams({ ...params, isStockAdditive: !isChecked })}
              label="Replace existing location stock"
              type="white"
            />
          </div>
        </div>
      ),
      renderSampleFile: ({ exportSampleFile }: { exportSampleFile: () => void }) => (
        <div className="text-left px-4 mb-4">
          <Typography type="text-sm" className="text-grey-500">
            <div>Import parts from a XLS or XLSX spreadsheet file.</div>
            <div>
              Click{' '}
              <a className="text-quipli-black-800 underline cursor-pointer" onClick={exportSampleFile}>
                here
              </a>{' '}
              to see the exact format of the worksheets.
            </div>
          </Typography>
        </div>
      ),
    }),
    [fetchCallback],
  );
};
